/* Page Connexion (/connexion) — Factumis app.
   Mot de passe (signIn.email) + magic link (signIn.magicLink).
   Si la 2FA est activée, signIn.email renvoie { twoFactorRedirect: true }
   sans session → étape code TOTP (ou code de secours) avant d'entrer.
   Sur succès → /dashboard (la garde redirige vers /onboarding
   s'il n'y a pas encore d'organisation). */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient, useSession } from '../lib/auth-client';
import { safeNext } from '../lib/nav';
import { AuthLayout } from './AuthLayout';
import { Button, Field, Input } from '../components/ui';
import { Icon } from '../lib/icons';

/* Mappe les erreurs Better Auth vers un message FR utilisateur. */
function frenchMessage(error: { code?: string; status?: number }): string {
  if (error.code === 'INVALID_EMAIL_OR_PASSWORD') return 'Email ou mot de passe incorrect.';
  if (error.code === 'EMAIL_NOT_VERIFIED') return 'Vérifiez votre email avant de vous connecter.';
  if (error.status === 429) return 'Trop de tentatives, réessayez dans un instant.';
  return 'Une erreur est survenue, réessayez.';
}

type Step = 'credentials' | 'totp' | 'backup';

export default function Connexion() {
  const navigate = useNavigate();
  const { refetch } = useSession();
  const [searchParams] = useSearchParams();
  const next = safeNext(searchParams.get('next'));

  /* Après une authentification réussie, le store de session réactif (useSession)
     est rafraîchi de façon asynchrone et NON attendue par signIn/verify. Naviguer
     immédiatement faisait lire à RequireSession un état périmé (data:null,
     isPending:false) → redirection vers /connexion (le « rebond » au login). On
     attend donc explicitement le refetch avant de naviguer : le garde voit alors
     une session établie (avec son org active, posée côté serveur). */
  async function enterApp() {
    // On attend le refetch pour éviter le rebond, mais un échec (réseau/500) ne
    // doit pas laisser l'utilisateur bloqué sur /connexion : on navigue quand même,
    // RequireSession refait le fetch et affiche son panneau « service indisponible »
    // si besoin.
    try {
      await refetch();
    } catch {
      /* la garde refera le fetch */
    }
    navigate(next);
  }
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  // Étape 2FA
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const busy = loading || magicLoading;

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    setLoading(true);
    const { data, error } = await authClient.signIn.email({ email, password });
    setLoading(false);
    if (error) {
      setErr(frenchMessage(error));
      return;
    }
    // 2FA activée : pas de session, un cookie « en attente » (10 min) est posé.
    if (data && typeof data === 'object' && 'twoFactorRedirect' in data) {
      setCode('');
      setStep('totp');
      return;
    }
    await enterApp();
  }

  async function onMagicLink() {
    if (!email) {
      setErr('Saisissez votre email pour recevoir un lien de connexion.');
      return;
    }
    setErr(null);
    setMagicLoading(true);
    const { error } = await authClient.signIn.magicLink({ email, callbackURL: next });
    setMagicLoading(false);
    if (error) {
      setErr(frenchMessage(error));
      return;
    }
    setMagicSent(true);
  }

  /* Retour à l'étape identifiants (cookie 2FA expiré). */
  function backToCredentials(message: string | null) {
    setStep('credentials');
    setPassword('');
    setCode('');
    setTrustDevice(false);
    setErr(message);
  }

  function switchStep(next: Step) {
    setStep(next);
    setCode('');
    setErr(null);
  }

  async function onVerify(ev: FormEvent) {
    ev.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || codeLoading) return;
    setErr(null);
    setCodeLoading(true);
    const { error } =
      step === 'backup'
        ? await authClient.twoFactor.verifyBackupCode({ code: trimmed })
        : await authClient.twoFactor.verifyTotp({ code: trimmed, trustDevice });
    setCodeLoading(false);
    if (error) {
      // Cookie « en attente » expiré (10 min) → repartir des identifiants.
      // Un code erroné renvoie aussi 401, mais avec INVALID_CODE /
      // INVALID_BACKUP_CODE — on distingue donc par le code d'erreur.
      if (error.code === 'INVALID_TWO_FACTOR_COOKIE') {
        backToCredentials('Session expirée, reconnectez-vous.');
        return;
      }
      setErr('Code invalide, réessayez.');
      return;
    }
    await enterApp();
  }

  const linkBtnStyle = {
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
  } as const;

  if (step !== 'credentials') {
    const isBackup = step === 'backup';
    return (
      <AuthLayout
        title="Vérification"
        subtitle={
          isBackup
            ? 'Saisissez l’un de vos codes de secours.'
            : 'Saisissez le code de votre application d’authentification.'
        }
      >
        {err ? (
          <div className="auth-error" role="alert">
            <Icon name="alert" size={16} />
            {err}
          </div>
        ) : null}

        <form className="auth-form" onSubmit={onVerify} noValidate>
          <Field label={isBackup ? 'Code de secours' : 'Code à 6 chiffres'}>
            <Input
              key={step}
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={isBackup ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              maxLength={isBackup ? undefined : 6}
              placeholder={isBackup ? undefined : '123456'}
              autoFocus
              disabled={codeLoading}
              required
            />
          </Field>
          {!isBackup ? (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 12,
                fontSize: 13.5,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                disabled={codeLoading}
              />
              Faire confiance à cet appareil pendant 30 jours
            </label>
          ) : null}
          <div className="auth-actions">
            <Button type="submit" full loading={codeLoading} disabled={codeLoading}>
              Vérifier
            </Button>
          </div>
        </form>

        <div className="auth-link-row">
          {isBackup ? (
            <button
              type="button"
              className="auth-link"
              style={linkBtnStyle}
              onClick={() => switchStep('totp')}
            >
              Revenir au code d’application
            </button>
          ) : (
            <button
              type="button"
              className="auth-link"
              style={linkBtnStyle}
              onClick={() => switchStep('backup')}
            >
              Utiliser un code de secours
            </button>
          )}
        </div>

        <div className="auth-foot-row">
          <button
            type="button"
            className="auth-link"
            style={linkBtnStyle}
            onClick={() => backToCredentials(null)}
          >
            Revenir à la connexion
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Connexion" subtitle="Accédez à votre espace de facturation.">
      {err ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {err}
        </div>
      ) : null}

      {magicSent ? (
        <p className="auth-sub">Lien envoyé, consultez vos mails.</p>
      ) : (
        <>
          <form className="auth-form" onSubmit={onSubmit} noValidate>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="vous@entreprise.fr"
                disabled={busy}
                required
              />
            </Field>
            <Field label="Mot de passe">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={busy}
                required
              />
            </Field>
            <div className="auth-actions">
              <Button type="submit" full loading={loading} disabled={busy}>
                Se connecter
              </Button>
            </div>
          </form>

          <div className="auth-link-row">
            <Link to="/oubli-mot-de-passe" className="auth-link">
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="auth-alt">ou</div>

          <Button
            type="button"
            variant="soft"
            full
            icon="send"
            loading={magicLoading}
            disabled={busy}
            onClick={onMagicLink}
          >
            Recevoir un lien de connexion
          </Button>

          <div className="auth-foot-row">
            Pas encore de compte ?{' '}
            <Link
              to={next !== '/dashboard' ? `/inscription?next=${encodeURIComponent(next)}` : '/inscription'}
              className="auth-link"
            >
              Créer un compte
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
